import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { surveysService } from "../services/surveys";
import { DistributionMode } from "@clawplete/shared/types";

interface Question {
  id: string;
  question_text: string;
  options: string[];
}

export default function SurveyEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [budget, setBudget] = useState("");
  const [targetResponses, setTargetResponses] = useState(100);
  const [distributionMode, setDistributionMode] = useState<DistributionMode>(DistributionMode.EFFICIENT);
  const [questions, setQuestions] = useState<Question[]>([
    { id: crypto.randomUUID(), question_text: "", options: ["", ""] },
  ]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Load existing survey data
  const { data: surveyData } = useQuery({
    queryKey: ["survey", id],
    queryFn: () => surveysService.get(id!),
    enabled: !!id,
  });

  // Populate form with existing data
  useEffect(() => {
    if (surveyData?.survey) {
      const survey = surveyData.survey;
      setTitle(survey.title || "");
      setDescription(survey.description || "");
      setBudget(survey.budget?.toString() || "");
      setTargetResponses(survey.target_responses || 100);
      setDistributionMode((survey.distribution_mode as DistributionMode) || DistributionMode.EFFICIENT);

      // Convert questions to form format
      if (survey.questions && survey.questions.length > 0) {
        const formattedQuestions = survey.questions.map((q) => ({
          id: q.id,
          question_text: q.question_text || "",
          options: q.options?.map((o) => o.option_text || "") || ["", ""],
        }));
        setQuestions(formattedQuestions);
      }

      setIsLoading(false);
    }
  }, [surveyData]);

  const updateMutation = useMutation({
    mutationFn: (data: { title: string; description?: string; budget: string; target_responses: number; distribution_mode: DistributionMode; questions: Array<{ question_text: string; options: string[] }> }) =>
      surveysService.update(id!, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["survey", id] });
      queryClient.invalidateQueries({ queryKey: ["surveys"] });
      navigate(`/surveys/${id}`);
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || "Failed to update survey");
    },
  });

  const addQuestion = () => {
    setQuestions([
      ...questions,
      { id: crypto.randomUUID(), question_text: "", options: ["", ""] },
    ]);
  };

  const removeQuestion = (id: string) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((q) => q.id !== id));
    }
  };

  const updateQuestionText = (id: string, text: string) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, question_text: text } : q))
    );
  };

  const addOption = (questionId: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId ? { ...q, options: [...q.options, ""] } : q
      )
    );
  };

  const removeOption = (questionId: string, optionIndex: number) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? { ...q, options: q.options.filter((_, i) => i !== optionIndex) }
          : q
      )
    );
  };

  const updateOption = (questionId: string, optionIndex: number, value: string) => {
    setQuestions(
      questions.map((q) =>
        q.id === questionId
          ? {
              ...q,
              options: q.options.map((opt, i) =>
                i === optionIndex ? value : opt
              ),
            }
          : q
      )
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    if (!budget || parseFloat(budget) <= 0) {
      setError("Budget must be greater than 0");
      return;
    }
    if (targetResponses < 1) {
      setError("Target responses must be at least 1");
      return;
    }

    const validQuestions = questions.filter((q) => q.question_text.trim());
    if (validQuestions.length === 0) {
      setError("At least one question is required");
      return;
    }

    for (const q of validQuestions) {
      const validOptions = q.options.filter((o) => o.trim());
      if (validOptions.length < 2) {
        setError("Each question must have at least 2 options");
        return;
      }
    }

    // Prepare data
    const surveyData = {
      title: title.trim(),
      description: description.trim() || undefined,
      budget,
      target_responses: targetResponses,
      distribution_mode: distributionMode,
      questions: validQuestions.map((q) => ({
        question_text: q.question_text.trim(),
        options: q.options.filter((o) => o.trim()),
      })),
    };

    updateMutation.mutate(surveyData);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-400">Loading survey...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-100 mb-8">Edit Survey</h1>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 text-red-400 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-100 mb-4">Basic Information</h2>
          <div className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                Title *
              </label>
              <input
                id="title"
                type="text"
                className="input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter survey title"
                required
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                Description
              </label>
              <textarea
                id="description"
                className="input"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter survey description (optional)"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-300 mb-2">
                  Budget (USDC) *
                </label>
                <input
                  id="budget"
                  type="number"
                  className="input"
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  placeholder="100"
                  min="1"
                  step="0.01"
                  required
                />
              </div>

              <div>
                <label htmlFor="targetResponses" className="block text-sm font-medium text-gray-300 mb-2">
                  Target Responses *
                </label>
                <input
                  id="targetResponses"
                  type="number"
                  className="input"
                  value={targetResponses}
                  onChange={(e) => setTargetResponses(parseInt(e.target.value) || 1)}
                  min="1"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="distributionMode" className="block text-sm font-medium text-gray-300 mb-2">
                Distribution Mode
              </label>
              <select
                id="distributionMode"
                className="input"
                value={distributionMode}
                onChange={(e) => setDistributionMode(e.target.value as DistributionMode)}
              >
                <option value={DistributionMode.EFFICIENT}>Efficient</option>
                <option value={DistributionMode.ASAP}>ASAP</option>
                <option value={DistributionMode.ECO}>Eco</option>
              </select>
              <p className="mt-1 text-sm text-gray-500">
                {distributionMode === DistributionMode.EFFICIENT && "Balanced distribution for optimal results"}
                {distributionMode === DistributionMode.ASAP && "Fastest distribution possible"}
                {distributionMode === DistributionMode.ECO && "Lowest cost distribution"}
              </p>
            </div>
          </div>
        </div>

        {/* Questions */}
        <div className="card">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-100">Questions</h2>
            <button
              type="button"
              onClick={addQuestion}
              className="btn btn-secondary"
            >
              Add Question
            </button>
          </div>

          <div className="space-y-6">
            {questions.map((question, qIndex) => (
              <div key={question.id} className="p-4 border border-dark-border rounded-lg bg-dark-800">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-medium text-gray-100">Question {qIndex + 1}</h3>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(question.id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Question Text *
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={question.question_text}
                      onChange={(e) => updateQuestionText(question.id, e.target.value)}
                      placeholder="Enter your question"
                    />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-medium text-gray-300">
                        Options * (at least 2)
                      </label>
                      {question.options.length < 10 && (
                        <button
                          type="button"
                          onClick={() => addOption(question.id)}
                          className="text-primary-300 hover:text-primary-200 text-sm"
                        >
                          Add Option
                        </button>
                      )}
                    </div>
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => (
                        <div key={oIndex} className="flex gap-2">
                          <input
                            type="text"
                            className="input flex-1"
                            value={option}
                            onChange={(e) => updateOption(question.id, oIndex, e.target.value)}
                            placeholder={`Option ${oIndex + 1}`}
                          />
                          {question.options.length > 2 && (
                            <button
                              type="button"
                              onClick={() => removeOption(question.id, oIndex)}
                              className="text-red-400 hover:text-red-300 px-2"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/surveys/${id}`)}
            className="btn btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
}
